import { getPlatformStatus } from './platformFilter';

const DIMENSIONS = ['category', 'age', 'income', 'landmark', 'scene'];
const cleanArea = value => String(value || '').replace(/\s+/g, '').replace(/(街道办事处|地区办事处|街道|地区|乡|镇)$/u, '');
const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getStoreKey = store => [store.name, store.lng, store.lat].map(value => String(value || '')).join('|');

const areaMap = rows => {
  const map = new Map();
  (rows || []).forEach(row => {
    [row.street, row.area_name, row.district, row.city, row.province].filter(Boolean)
      .forEach(name => map.set(cleanArea(name), row));
  });
  return map;
};

const findAreaRow = (map, store) => {
  const candidates = [store.street, store.district, store.adname, store.city, store.cityname, store.province, store.pname];
  for (const candidate of candidates) {
    const row = map.get(cleanArea(candidate));
    if (row) return row;
  }
  return undefined;
};

export const getEffectiveWeights = (weights, availability, enabledDimensions = {}) => {
  const active = DIMENSIONS.filter(key => enabledDimensions[key] !== false && availability[key] && Number(weights[key] || 0) > 0);
  const total = active.reduce((sum, key) => sum + Number(weights[key] || 0), 0);
  return DIMENSIONS.reduce((result, key) => ({
    ...result,
    [key]: total && active.includes(key) ? Number(weights[key] || 0) / total : 0,
  }), {});
};

const weightedRegionalScore = ({ values, weights, enabledDimensions }) => {
  const availability = DIMENSIONS.reduce((result, key) => ({ ...result, [key]: numberOrNull(values[key]) !== null }), {});
  const effectiveWeights = getEffectiveWeights(weights, availability, enabledDimensions);
  const active = DIMENSIONS.filter(key => effectiveWeights[key] > 0);
  if (!active.length) return { score: null, effectiveWeights, availability };
  return {
    score: active.reduce((sum, key) => sum + numberOrNull(values[key]) * effectiveWeights[key], 0),
    effectiveWeights,
    availability,
  };
};

const selectedLabels = selectedCategory => Array.isArray(selectedCategory)
  ? selectedCategory.flat(Infinity).filter(Boolean).map(String) : [];

const categoryCoefficient = (store, selectedCategory) => {
  const labels = selectedLabels(selectedCategory);
  if (!labels.length) return 1;
  const text = [store.name, store.parentType, store.childType, store.type].filter(Boolean).join('/');
  if (labels.some(label => text.includes(label))) return 1.3;
  const weakKeywords = {
    '日常护理': ['个护', '日化', '美妆'],
    '休闲零食': ['零食', '食品'],
    '饮料冲调': ['饮料', '咖啡', '茶饮'],
  };
  if (labels.some(label => (weakKeywords[label] || []).some(keyword => text.includes(keyword)))) return 1.05;
  return 0.7;
};

const platformInfo = (store, meituanConfidence, elemeConfidence) => {
  const meituanSimilarity = Number(store.mapping_meituan_similarity || 0);
  const elemeSimilarity = Number(store.mapping_eleme_similarity || 0);
  const { onMeituan: meituan, onEleme: eleme } = getPlatformStatus(store, meituanConfidence, elemeConfidence);
  return {
    meituan,
    eleme,
    coefficient: meituan && eleme ? 1.2 : (meituan || eleme ? 1.05 : 0.85),
    confidence: Math.max(meituan ? meituanSimilarity : 0, eleme ? elemeSimilarity : 0),
  };
};

const topTags = (row, definitions) => !row ? [] : definitions
  .map(([key, label]) => ({ label, value: Number(row[key] || 0) }))
  .filter(item => item.value > 0).sort((a, b) => b.value - a.value).slice(0, 2);

const regionalIdentity = row => {
  const areaName = row.street || row.area_name || row.district || row.city || row.province || '';
  return [row.province, row.city, row.district, areaName].map(cleanArea).join('|');
};

export const buildCompositeDemandData = ({ demandData = [], populationData = [], incomeData = [], weights,
  enabledDimensions = { category: true, age: true, income: true } }) => {
  const regions = new Map();
  const merge = (rows, dimension, field) => rows.forEach(row => {
    const key = regionalIdentity(row);
    const current = regions.get(key) || {
      province: row.province, city: row.city, district: row.district, street: row.street,
      area_name: row.area_name || row.street || row.district || row.city || row.province,
      values: { category: null, age: null, income: null, landmark: null, scene: null },
    };
    current.values[dimension] = numberOrNull(row[field]);
    regions.set(key, { ...current, ...row, values: current.values });
  });
  merge(demandData, 'category', 'normalized_demand');
  merge(populationData, 'age', 'normalized_population');
  merge(incomeData, 'income', 'normalized_income');
  return [...regions.values()].map(row => {
    const calculated = weightedRegionalScore({ values: row.values, weights, enabledDimensions });
    return {
      ...row,
      normalized_demand: calculated.score,
      streetDemandScore: calculated.score === null ? null : Math.round(calculated.score),
      effectiveWeights: calculated.effectiveWeights,
      availableDimensions: calculated.availability,
      layerType: 'composite',
    };
  });
};

export const enrichStores = ({ stores, demandData, populationData, incomeData, weights,
  selectedCategory, meituanConfidence, elemeConfidence,
  enabledDimensions = { category: true, age: true, income: true } }) => {
  const demand = areaMap(demandData);
  const population = areaMap(populationData);
  const income = areaMap(incomeData);
  return (stores || []).map(store => {
    const demandRow = findAreaRow(demand, store);
    const populationRow = findAreaRow(population, store);
    const incomeRow = findAreaRow(income, store);
    // 街道综合需求分可由任一已启用的街道画像维度计算，
    // 不应因为尚未选择品类需求而把年龄/收入计算结果强制置空。
    const hasStreetDemand = Boolean(store.street && (demandRow || populationRow || incomeRow));
    const calculated = weightedRegionalScore({
      values: {
        category: numberOrNull(demandRow?.normalized_demand),
        age: numberOrNull(populationRow?.normalized_population),
        income: numberOrNull(incomeRow?.normalized_income),
        landmark: numberOrNull(store.landmarkScore),
        scene: null,
      }, weights, enabledDimensions,
    });
    const categoryMatch = categoryCoefficient(store, selectedCategory);
    const platform = platformInfo(store, meituanConfidence, elemeConfidence);
    const confidenceCoefficient = 0.9 + platform.confidence * 0.2;
    const demandScore = calculated.score === null ? null
      : calculated.score * categoryMatch * platform.coefficient * confidenceCoefficient * 1.5;
    return {
      ...store,
      _key: getStoreKey(store),
      demandScore: demandScore === null ? null : Math.round(demandScore),
      streetDemandScore: !hasStreetDemand || calculated.score === null ? null : Math.round(calculated.score),
      effectiveWeights: calculated.effectiveWeights,
      demandAreaName: demandRow?.area_name || demandRow?.street || demandRow?.district || demandRow?.city || demandRow?.province || '',
      categoryCoefficient: categoryMatch,
      platformCoefficient: platform.coefficient,
      confidenceCoefficient,
      onMeituan: platform.meituan,
      onEleme: platform.eleme,
      populationMatched: Boolean(populationRow),
      incomeMatched: Boolean(incomeRow),
      landmarkMatched: numberOrNull(store.landmarkScore) !== null,
      ageTags: topTags(populationRow, [['youth_population', '少年群体'], ['labor_population', '15-59岁群体'], ['elderly_population', '老年群体']]),
      incomeTags: topTags(incomeRow, [['high_income_pop', '高收入'], ['middle_income_pop', '中收入'], ['low_income_pop', '低收入']]),
    };
  }).sort((a, b) => a.demandScore === null ? 1 : (b.demandScore === null ? -1 : b.demandScore - a.demandScore));
};
